/**
 * Catálogo de tipos de modelo.
 *
 * El campo `programas` es un string descriptivo legacy sin relación con `Area`.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

// stores/tipoModelo.ts
import { defineStore } from 'pinia'
import TipoModelo from '@/interfaces/TipoModelo'
import { obtenerTipoModelos, obtenerTipoModeloPorId, crearTipoModeloService, actualizarTipoModeloService, eliminarTipoModeloService } from '@/services/TipoModeloService'

/** Store `GestionTipoModelos`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionTipoModelos = defineStore('GestionTipoModelos', {
  state: () => ({
    tipos: [] as TipoModelo[],
    tipoActual: null as TipoModelo | null,
    urlTipoModelos: `${process.env.VUE_APP_API_BASE_URL}/api/tipo-modelos`
  }),

  actions: {
    async getTipoModelos(): Promise<TipoModelo[]> {
      const lista = await obtenerTipoModelos(this.urlTipoModelos)
      this.tipos = lista
      return lista
    },

    async getTipoModeloById(id: number): Promise<TipoModelo | null> {
      const tipo = await obtenerTipoModeloPorId(this.urlTipoModelos, id)
      this.tipoActual = tipo
      return tipo
    },

    async crearTipoModelo(tipo: Omit<TipoModelo, 'id_tipo_modelo'>): Promise<TipoModelo | null> {
      const creado = await crearTipoModeloService(this.urlTipoModelos, tipo)
      if (!creado) return null
      this.tipos.push(creado)
      this.tipoActual = creado
      return creado
    },

    async updateTipoModelo(tipo: TipoModelo): Promise<boolean> {
      if (!tipo.id_tipo_modelo) return false
      const actualizado = await actualizarTipoModeloService(this.urlTipoModelos, tipo.id_tipo_modelo, { tipo: tipo.tipo })
      if (!actualizado) return false
      const idx = this.tipos.findIndex(t => t.id_tipo_modelo === actualizado.id_tipo_modelo)
      if (idx !== -1) this.tipos[idx] = actualizado
      if (this.tipoActual?.id_tipo_modelo === actualizado.id_tipo_modelo) {
        this.tipoActual = actualizado
      }
      return true
    },

    async eliminarTipoModelo(id: number): Promise<boolean> {
      const ok = await eliminarTipoModeloService(this.urlTipoModelos, id)
      if (!ok) return false
      this.tipos = this.tipos.filter(t => t.id_tipo_modelo !== id)
      if (this.tipoActual?.id_tipo_modelo === id) {
        this.tipoActual = null
      }
      return true
    }
  }
})
